/**
 * Vision OCR Service for Arabic Text Extraction
 * Connects React frontend with folio-smart-help vision service
 * Uses qwen2.5vl:3b model for advanced text extraction
 */

const API_BASE_URL = 'http://localhost:3001';

class VisionOcrService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Check if the Vision service is available
   * @returns {Promise<boolean>} Service availability status
   */
  async isServiceAvailable() {
    try {
      const response = await fetch(`${this.baseURL}/api/v1/folio-help/health`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.success && data.data?.status === 'healthy';
    } catch (error) {
      console.warn('Vision service health check failed:', error.message);
      return false;
    }
  }

  /**
   * Extract Arabic text from image using Vision model
   * @param {File} imageFile - The image file to process
   * @param {Function} onProgress - Optional progress callback
   * @param {Object} options - Extraction options
   * @returns {Promise<Object>} Extraction results
   */
  async extractArabicText(imageFile, onProgress = null, options = {}) {
    try {
      // Validate input
      if (!imageFile) {
        throw new Error('لم يتم تحديد صورة');
      }

      // Check file type
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
      ];
      if (!allowedTypes.includes(imageFile.type)) {
        throw new Error('نوع الملف غير مدعوم. يرجى استخدام JPG, PNG, أو WebP');
      }

      // Check file size (limit to 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (imageFile.size > maxSize) {
        throw new Error('حجم الملف كبير جداً. الحد الأقصى 10 ميجابايت');
      }

      if (onProgress) {
        onProgress('جاري تحضير الصورة...');
      }

      // Prepare form data
      const formData = new FormData();
      formData.append('image', imageFile);

      // Set extraction options
      const extractionType = options.extractionType || 'structured';
      const language = options.language || 'mixed';
      const fallbackEnabled = options.fallbackEnabled !== false;
      const selectedModel = options.selectedModel || '';

      formData.append('extractionType', extractionType);
      formData.append('language', language);
      formData.append('fallbackEnabled', fallbackEnabled.toString());

      // Parse and add provider/model if selected
      if (selectedModel) {
        const [provider, model] = selectedModel.split('|');
        if (provider && model) {
          formData.append('provider', provider);
          formData.append('model', model);
        }
      }

      if (onProgress) {
        onProgress('جاري إرسال الصورة للنموذج البصري...');
      }

      // Send request to Vision service
      const response = await fetch(
        `${this.baseURL}/api/v1/folio-help/vision/extract`,
        {
          method: 'POST',
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error?.message || `خطأ في الخدمة: ${response.status}`,
        );
      }

      if (onProgress) {
        onProgress('جاري معالجة النتائج...');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error?.message || 'فشل في استخراج النص');
      }

      if (onProgress) {
        onProgress('تم الانتهاء بنجاح');
      }

      // Return raw results from backend without transformation
      return result;
    } catch (error) {
      console.error('Vision extraction failed:', error);
      throw new Error(`فشل في استخراج النص: ${error.message}`);
    }
  }

  /**
   * Extract text with specific type
   * @param {File} imageFile - The image file
   * @param {string} extractionType - Type of extraction
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Extraction results
   */
  async extractWithType(imageFile, extractionType, onProgress = null) {
    return this.extractArabicText(imageFile, onProgress, { extractionType });
  }

  /**
   * Extract basic text only
   * @param {File} imageFile - The image file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Text extraction results
   */
  async extractText(imageFile, onProgress = null) {
    return this.extractWithType(imageFile, 'text', onProgress);
  }

  /**
   * Extract entities from image
   * @param {File} imageFile - The image file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Entities extraction results
   */
  async extractEntities(imageFile, onProgress = null) {
    return this.extractWithType(imageFile, 'entities', onProgress);
  }

  /**
   * Extract structured data from image
   * @param {File} imageFile - The image file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Structured extraction results
   */
  async extractStructured(imageFile, onProgress = null) {
    return this.extractWithType(imageFile, 'structured', onProgress);
  }

  /**
   * Perform full analysis of academic document
   * @param {File} imageFile - The image file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Full analysis results
   */
  async extractFullAnalysis(imageFile, onProgress = null) {
    return this.extractWithType(imageFile, 'full_analysis', onProgress);
  }

  /**
   * Transform Vision results to match paddleOCR format
   * @param {Object} visionResult - Raw Vision results
   * @param {string} extractionType - Type of extraction performed
   * @returns {Object} Transformed results
   */
  transformResults(visionResult, extractionType) {
    const { data } = visionResult;
    const { extractedText, entities, metadata, structuredData } = data;

    // Base result structure
    const result = {
      success: true,
      text: extractedText || '',
      confidence: Math.round((metadata?.confidence || 0.8) * 100),
      language: metadata?.language || 'mixed',
      processingTime: metadata?.processingTime || 0,
      metadata: {
        modelUsed: metadata?.modelUsed || 'qwen2.5vl:3b',
        extractionType,
        fallbackUsed: metadata?.fallbackUsed || false,
        imageCompressed: metadata?.imageCompressed || false,
        originalSize: metadata?.originalSize || 0,
        processedSize: metadata?.processedSize || 0,
      },
    };

    // Transform based on extraction type
    switch (extractionType) {
      case 'text':
        result.bookInfo = this.extractBasicBookInfo(extractedText);
        break;

      case 'entities':
        result.bookInfo = this.transformEntitiesResult(entities);
        result.entities = entities;
        break;

      case 'structured':
        result.bookInfo = this.transformStructuredResult(entities || {});
        result.regions = this.extractRegions(entities || {});
        break;

      case 'full_analysis':
        if (data.structuredData?.template_extraction) {
          result.bookInfo = this.transformFullAnalysisResult(
            data.structuredData,
          );
          result.templateData = data.structuredData;
        } else {
          result.bookInfo = this.transformEntitiesResult(entities || {});
        }
        break;

      default:
        result.bookInfo = this.extractBasicBookInfo(extractedText);
    }

    return result;
  }

  /**
   * Extract basic book information from text
   * @param {string} text - Extracted text
   * @returns {Object} Basic book information
   */
  extractBasicBookInfo(text) {
    if (!text) {
      return {
        title: '',
        author: '',
        publisher: '',
        year: '',
        isbn: '',
        edition: '',
      };
    }

    const lines = text.split('\n').filter((line) => line.trim());

    return {
      title: lines[0] || '',
      author: lines[1] || '',
      publisher: '',
      year: this.extractYear(text),
      isbn: this.extractISBN(text),
      edition: '',
    };
  }

  /**
   * Transform entities result to book info
   * @param {Object} entities - Extracted entities
   * @returns {Object} Book information
   */
  transformEntitiesResult(entities) {
    if (!entities) {
      return this.getEmptyBookInfo();
    }

    return {
      title: entities.title || '',
      author: Array.isArray(entities.authors)
        ? entities.authors[0]
        : entities.authors || '',
      publisher: entities.publisher || entities.university || '',
      year: entities.publication_year || entities.year || '',
      isbn: entities.isbn || entities.barcode || '',
      edition: entities.edition || entities.degree || '',
    };
  }

  /**
   * Transform structured result to book info
   * @param {Object} structured - Structured data
   * @returns {Object} Book information
   */
  transformStructuredResult(structured) {
    if (!structured) {
      return this.getEmptyBookInfo();
    }

    return {
      title: structured.title || '',
      author: Array.isArray(structured.authors)
        ? structured.authors[0]
        : structured.authors || '',
      publisher: structured.publisher || structured.university || '',
      year: structured.year || structured.publication_year || '',
      isbn: structured.isbn || '',
      edition: structured.edition || structured.degree || '',
    };
  }

  /**
   * Transform full analysis result to book info
   * @param {Object} fullAnalysis - Full analysis data
   * @returns {Object} Book information
   */
  transformFullAnalysisResult(fullAnalysis) {
    const instance = fullAnalysis.template_extraction?.instance;
    if (!instance) {
      return this.getEmptyBookInfo();
    }

    const contributors = instance.contributors || [];
    const author =
      contributors.find((c) => c.primary)?.name || contributors[0]?.name || '';

    return {
      title: instance.title || '',
      author,
      publisher:
        instance.publication?.[0]?.publisher ||
        instance.customFields?.universityName ||
        '',
      year:
        instance.publication?.[0]?.dateOfPublication ||
        instance.customFields?.Discussionhistory ||
        '',
      isbn: instance.identifiers?.[0]?.value || '',
      edition: instance.customFields?.AcademicDegree || '',
    };
  }

  /**
   * Extract regions from entities
   * @param {Object} entities - Extracted entities
   * @returns {Object} Regions information
   */
  extractRegions(entities) {
    return {
      title: { text: entities.title || '' },
      author: {
        text: Array.isArray(entities.authors)
          ? entities.authors[0]
          : entities.authors || '',
      },
      publisher: { text: entities.publisher || entities.university || '' },
    };
  }

  /**
   * Extract year from text
   * @param {string} text - Text to search
   * @returns {string} Extracted year
   */
  extractYear(text) {
    const yearPattern = /(?:19|20)\d{2}/g;
    const matches = text.match(yearPattern);
    return matches ? matches[matches.length - 1] : '';
  }

  /**
   * Extract ISBN from text
   * @param {string} text - Text to search
   * @returns {string} Extracted ISBN
   */
  extractISBN(text) {
    const isbnPattern = /(?:ISBN|isbn|ردمك)[:\s]*([0-9-]+)/gi;
    const match = text.match(isbnPattern);
    return match
      ? match[0].replace(/(?:ISBN|isbn|ردمك)[:\s]*/gi, '').trim()
      : '';
  }

  /**
   * Get empty book info structure
   * @returns {Object} Empty book information
   */
  getEmptyBookInfo() {
    return {
      title: '',
      author: '',
      publisher: '',
      year: '',
      isbn: '',
      edition: '',
    };
  }

  /**
   * Get available extraction types
   * @returns {Array} Available extraction types
   */
  getAvailableExtractionTypes() {
    return [
      { value: 'text', label: 'استخراج النص الأساسي' },
      { value: 'entities', label: 'استخراج الكيانات' },
      { value: 'structured', label: 'استخراج منظم' },
      { value: 'full_analysis', label: 'التحليل الشامل' },
    ];
  }
}

// Export singleton instance
const visionOcrService = new VisionOcrService();
export default visionOcrService;
